import { useGetList } from "ra-core";
import { useMemo, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  Minus,
  Printer,
  Sparkles,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { callByoAi } from "../ai/byoai";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { ReportPageHeader } from "./ReportChrome";
import type { Contact, ContactNote, Deal, DealNote } from "../types";

/**
 * Client Monthly Scorecard - the one-pager an agency hands its client:
 * month-over-month growth numbers computed live from the CRM, a short
 * recommendations block, and (with a BYO AI key) a narrative paragraph.
 * Print-ready: the browser's print-to-PDF is the export.
 */

const monthKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const inMonth = (iso: string | null | undefined, key: string) =>
  !!iso && iso.slice(0, 7) === key;

const lifecycleRank = ["subscriber", "lead", "mql", "sql", "opportunity", "customer"];

export const ScorecardTab = () => {
  const config = useConfigurationContext();
  const { currency, title, lightModeLogo, integrations } = config;
  const [offset, setOffset] = useState(0); // 0 = current month
  const [narrative, setNarrative] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1000 },
  });
  const { data: deals = [] } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
  });
  const { data: contactNotes = [] } = useGetList<ContactNote>("contact_notes", {
    pagination: { page: 1, perPage: 2000 },
  });
  const { data: dealNotes = [] } = useGetList<DealNote>("deal_notes", {
    pagination: { page: 1, perPage: 2000 },
  });

  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      return {
        offset: i,
        key: monthKey(d),
        label: d.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
      };
    });
  }, []);
  const current = months[offset]!;
  const prior = months[offset + 1] ?? months[offset]!;

  const metricsFor = (key: string) => {
    const newContacts = contacts.filter((c) => inMonth(c.first_seen, key));
    const mqls = newContacts.filter(
      (c) =>
        lifecycleRank.indexOf(c.lifecycle_stage ?? "subscriber") >=
        lifecycleRank.indexOf("mql"),
    );
    const meetings =
      contactNotes.filter(
        (n) => n.activity_type === "meeting" && inMonth(n.date, key),
      ).length +
      dealNotes.filter(
        (n) => n.activity_type === "meeting" && inMonth(n.date, key),
      ).length;
    const created = deals.filter((d) => inMonth(d.created_at, key));
    const won = deals.filter(
      (d) => d.stage === "won" && inMonth(d.closed_at, key),
    );
    return {
      newContacts: newContacts.length,
      byChannel: newContacts.reduce<Record<string, number>>((acc, c) => {
        const ch = [...(c.touches ?? [])].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        )[0]?.channel;
        if (ch) acc[ch] = (acc[ch] ?? 0) + 1;
        return acc;
      }, {}),
      mqls: mqls.length,
      meetings,
      pipelineCreated: created.reduce((s, d) => s + d.amount, 0),
      dealsCreated: created.length,
      closedWon: won.reduce((s, d) => s + d.amount, 0),
      wonCount: won.length,
    };
  };

  const cur = useMemo(
    () => metricsFor(current.key),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contacts, deals, contactNotes, dealNotes, current.key],
  );
  const prev = useMemo(
    () => metricsFor(prior.key),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contacts, deals, contactNotes, dealNotes, prior.key],
  );

  const fmt = (n: number) =>
    n.toLocaleString("en-US", {
      notation: "compact",
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 1,
    });

  const rows: { label: string; now: string; before: string; delta: number }[] = [
    { label: "New contacts", now: String(cur.newContacts), before: String(prev.newContacts), delta: cur.newContacts - prev.newContacts },
    { label: "MQLs added (MQL or beyond)", now: String(cur.mqls), before: String(prev.mqls), delta: cur.mqls - prev.mqls },
    { label: "Meetings logged", now: String(cur.meetings), before: String(prev.meetings), delta: cur.meetings - prev.meetings },
    { label: `Pipeline created (${cur.dealsCreated} deals)`, now: fmt(cur.pipelineCreated), before: fmt(prev.pipelineCreated), delta: cur.pipelineCreated - prev.pipelineCreated },
    { label: `Closed won (${cur.wonCount} deals)`, now: fmt(cur.closedWon), before: fmt(prev.closedWon), delta: cur.closedWon - prev.closedWon },
  ];

  // Conservative, data-grounded recommendations
  const recommendations: string[] = [];
  if ((cur.byChannel["paid"] ?? 0) === 0)
    recommendations.push(
      "No new contacts arrived from paid this month - worth reviewing whether campaigns are live and tracked.",
    );
  if (cur.mqls === 0 && cur.newContacts > 0)
    recommendations.push(
      "New contacts landed but none reached MQL - revisit scoring thresholds or nurture cadence.",
    );
  if (cur.meetings < prev.meetings)
    recommendations.push(
      "Meetings are down month-over-month - a booking-link push in active sequences usually recovers this.",
    );
  if (recommendations.length === 0)
    recommendations.push(
      "All core growth signals moved the right direction - keep current programs running.",
    );

  const hasAiKey = (integrations?.ai?.fields?.apiKey ?? "").trim() !== "";

  const generateNarrative = async () => {
    setGenerating(true);
    setNarrative(null);
    const summary = rows
      .map((r) => `${r.label}: ${r.now} (prior month ${r.before})`)
      .join("; ");
    const res = await callByoAi(
      integrations?.ai,
      "You write one short, plain-English paragraph (3-4 sentences) for a monthly client growth report. Confident but factual; no bullet lists; no invented numbers.",
      [
        {
          role: "user",
          text: `Write the summary paragraph for ${current.label}. Metrics: ${summary}. Top channels for new contacts: ${Object.entries(cur.byChannel).map(([k, v]) => `${k} ${v}`).join(", ") || "none"}.`,
        },
      ],
    );
    setNarrative(res.text);
    setGenerating(false);
  };

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <style>{`@media print {
        header, nav, [data-slot=tabs-list], .no-print, button { display: none !important; }
        main { padding: 0 !important; max-width: none !important; }
        .print-card { border: none !important; box-shadow: none !important; }
      }`}</style>

      <div className="no-print">
        <ReportPageHeader slug="scorecard" />
      </div>

      <div className="flex flex-wrap items-center gap-2 no-print">
        <Select
          value={String(offset)}
          onValueChange={(v) => {
            setOffset(Number(v));
            setNarrative(null);
          }}
        >
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {months.slice(0, 11).map((m) => (
              <SelectItem key={m.key} value={String(m.offset)}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => window.print()}>
          <Printer className="w-4 h-4" /> Print / save as PDF
        </Button>
        {hasAiKey ? (
          <Button
            variant="outline"
            size="sm"
            disabled={generating}
            onClick={generateNarrative}
          >
            <Sparkles className="w-4 h-4" />
            {generating ? "Writing…" : "AI narrative"}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">
            Add an AI key under Integrations for a written summary paragraph.
          </span>
        )}
      </div>

      <Card className="print-card">
        <CardContent className="p-6 flex flex-col gap-5">
          <div className="flex items-center gap-3 border-b border-border pb-4">
            <img src={lightModeLogo} alt={title} className="h-8" />
            <div>
              <h1 className="text-lg font-semibold leading-tight">
                {title} - Monthly growth scorecard
              </h1>
              <p className="text-sm text-muted-foreground">{current.label}</p>
            </div>
          </div>

          {narrative && (
            <p className="text-sm leading-6 border-l-2 border-primary/40 pl-3">
              {narrative}
            </p>
          )}

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1.5 font-medium">Metric</th>
                <th className="py-1.5 font-medium text-right">{current.label.split(" ")[0]}</th>
                <th className="py-1.5 font-medium text-right">{prior.label.split(" ")[0]}</th>
                <th className="py-1.5 font-medium text-right">Trend</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className="border-t border-border">
                  <td className="py-2">{r.label}</td>
                  <td className="py-2 text-right tabular-nums font-semibold">{r.now}</td>
                  <td className="py-2 text-right tabular-nums text-muted-foreground">{r.before}</td>
                  <td className="py-2 text-right">
                    {r.delta > 0 ? (
                      <ArrowUpRight className="w-4 h-4 inline text-[#8fb586]" />
                    ) : r.delta < 0 ? (
                      <ArrowDownRight className="w-4 h-4 inline text-[#c97a63]" />
                    ) : (
                      <Minus className="w-4 h-4 inline text-muted-foreground" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div>
            <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-1.5">
              New contacts by first-touch channel
            </h2>
            <div className="flex flex-wrap gap-2">
              {Object.entries(cur.byChannel).length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No attributed contacts this month.
                </p>
              )}
              {Object.entries(cur.byChannel)
                .sort((a, b) => b[1] - a[1])
                .map(([ch, n]) => (
                  <span
                    key={ch}
                    className="rounded-md border border-border px-2 py-1 text-xs capitalize"
                  >
                    {ch} <span className="font-semibold tabular-nums">{n}</span>
                  </span>
                ))}
            </div>
          </div>

          <div>
            <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-1.5">
              Recommendations
            </h2>
            <ul className="text-sm leading-6 list-disc pl-5">
              {recommendations.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>

          <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
            Generated from live CRM data · attribution uses first-touch ·
            closed-won uses actual close dates.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
